import { Supabase } from "../Utils/supabase.config";
import { uploadFile } from "./imageService";
import { generateVideoThumbnail } from "../Utils/videoThumbnails";
import { canPostAnnouncement } from "./communityService";

// Anonymous posting constants
const ANONYMOUS_COMMUNITY_ID = '4c88515c-51eb-40de-8e52-b9c8023b460c'; // "No-Judgment Zone"
const ANONYMOUS_USER_ID = 'e9e11754-95a5-43d4-a1e7-c448e4e2b5f6'; // "Anonymous User"

export const createOrUpdatePost = async (post)=>{
    try{
        // Store the actual user ID before any modifications
        const actualUserId = post.userId;
        const isEditMode = !!post.id;
        
        // Handle anonymous posting for "No-Judgment Zone" community
        // Only apply anonymous logic for new posts (not edits) or if created_by is not set
        if (post.community_id === ANONYMOUS_COMMUNITY_ID && actualUserId) {
            // For new posts, always use anonymous user
            // For edits, only change if created_by is not already set (backward compatibility)
            if (!isEditMode || !post.created_by) {
                // Set created_by to actual user ID to maintain who created the post
                post.created_by = actualUserId;
                // Set userId to anonymous user ID for display purposes
                post.userId = ANONYMOUS_USER_ID;
            }
        }
        
        // Check if user can post announcements in this community
        // Use actualUserId for permission check, not the potentially modified post.userId
        if (post.is_announcement && post.community_id) {
            const canPost = await canPostAnnouncement(actualUserId, post.community_id);
            if (!canPost.success || !canPost.data) {
                return { success: false, msg: "You don't have permission to post announcements in this community" };
            }
        }

        // Handle file upload - can be string, array, or object (for new uploads)
        // Arrays are already uploaded paths, so pass through
        if(post.file && typeof post.file == 'object' && !Array.isArray(post.file)){
            // This is a local file object that needs uploading
            const isImage = post?.file?.type=='image';
            const folderName = isImage? 'postImages': 'postVideos';

            // Keep original local video URI BEFORE we overwrite post.file with storage path
            const originalVideoUri = !isImage ? (post?.file?.uri || post?.file?.path || post?.fileUri) : undefined;

            // Upload primary media
            const fileResult = await uploadFile(folderName, post?.file?.uri, isImage, post?.file?.fileName);
            if(!fileResult.success){
                return fileResult;
            }
            post.file = fileResult.data;

            // For videos, generate and upload a thumbnail image
            if (!isImage) {
                try {
                    console.log('[Thumbnail] Original local URI:', originalVideoUri);
                    const thumbGen = await generateVideoThumbnail(originalVideoUri || '');
                    if (thumbGen.success && thumbGen.path) {
                        console.log('[Thumbnail] Uploading generated thumbnail:', thumbGen.path);
                        const thumbUpload = await uploadFile('postImages', thumbGen.path, true, `thumb_${Date.now()}.jpg`);
                        if (thumbUpload.success) {
                            console.log('[Thumbnail] Thumbnail uploaded at:', thumbUpload.data);
                            post.thumbnail = thumbUpload.data;
                        }
                    }
                } catch (e) {
                    console.log('[Thumbnail] Error during thumbnail pipeline:', e?.message || e);
                    // Swallow thumbnail errors; main upload already succeeded
                }
            }
        }
        // If post.file is an array, it's already uploaded paths - pass through as is
        
        const { data, error } = await Supabase
        .from('posts')
        .upsert(post)
        .select()
        .single();

        if(error){
            return {success: false, msg: "Could not create your post"};
        }
        return {success: true, data: data};

    }catch(error){
        return {success: false, msg: "Could not create your post"};
    }
}

// Create announcement post
export const createAnnouncement = async (post) => {
    try {
        // Validate announcement permissions
        if (post.community_id) {
            const canPost = await canPostAnnouncement(post.userId, post.community_id);
            if (!canPost.success || !canPost.data) {
                return { success: false, msg: "You don't have permission to post announcements in this community" };
            }
        }

        // Set announcement flags
        post.is_announcement = true;
        // is_pinned will be set separately if needed

        return await createOrUpdatePost(post);
    } catch (error) {
        return { success: false, msg: "Could not create announcement" };
    }
}

// Update announcement post
export const updateAnnouncement = async (post) => {
    try {
        // Validate announcement permissions
        if (post.community_id) {
            const canPost = await canPostAnnouncement(post.userId, post.community_id);
            if (!canPost.success || !canPost.data) {
                return { success: false, msg: "You don't have permission to update announcements in this community" };
            }
        }

        // Ensure announcement flags are set
        post.is_announcement = true;

        return await createOrUpdatePost(post);
    } catch (error) {
        return { success: false, msg: "Could not update announcement" };
    }
}

// Fetch announcements for a community
export const fetchCommunityAnnouncements = async (communityId, limit = 10) => {
    try {
        // Fetch pinned announcements first, then regular announcements
        const { data: pinned, error: pinnedError } = await Supabase
            .from('posts')
            .select(`
                *,
                user: users ( id, name, image ),
                community: communities ( name, is_linkallowed ),
                postLikes (*),
                comments (count)
            `)
            .eq('community_id', communityId)
            .eq('is_announcement', true)
            .eq('is_pinned', true)
            .eq('hidden', false)
            .order('created_at', { ascending: false });

        if (pinnedError) {
            return { success: false, msg: "Could not fetch announcements" };
        }

        const remaining = Math.max(0, limit - (pinned?.length || 0));

        const { data: regular, error: regularError } = await Supabase
            .from('posts')
            .select(`
                *,
                user: users ( id, name, image ),
                community: communities ( name, is_linkallowed ),
                postLikes (*),
                comments (count)
            `)
            .eq('community_id', communityId)
            .eq('is_announcement', true)
            .eq('is_pinned', false)
            .eq('hidden', false)
            .order('created_at', { ascending: false })
            .limit(remaining);

        if (regularError) {
            return { success: false, msg: "Could not fetch announcements" };
        }

        const data = [...(pinned || []), ...(regular || [])];
        
        return { success: true, data: data };
    } catch (error) {
        return { success: false, msg: "Could not fetch announcements" };
    }
}

// Fetch all announcements from all announcement communities (public access)
export const fetchAllPublicAnnouncements = async (limit = 50, offset = 0) => {
    try {
        // Fetch pinned announcements first
        const { data: pinned, error: pinnedError } = await Supabase
            .from('posts')
            .select(`
                *,
                user: users ( id, name, image ),
                community: communities ( name, is_linkallowed ),
                postLikes (*),
                comments (count)
            `)
            .eq('is_announcement', true)
            .eq('is_pinned', true)
            .eq('hidden', false)
            .order('created_at', { ascending: false });

        if (pinnedError) {
            return { success: false, msg: "Could not fetch announcements" };
        }

        const remaining = Math.max(0, limit - (pinned?.length || 0));

        // Then regular announcements with pagination window applied to remainder
        const { data: regular, error: regularError } = await Supabase
            .from('posts')
            .select(`
                *,
                user: users ( id, name, image ),
                community: communities ( name, is_linkallowed ),
                postLikes (*),
                comments (count)
            `)
            .eq('is_announcement', true)
            .eq('is_pinned', false)
            .eq('hidden', false)
            .order('created_at', { ascending: false })
            .range(offset, offset + remaining - 1);
        
        if (regularError) {
            return { success: false, msg: "Could not fetch announcements" };
        }

        const data = [...(pinned || []), ...(regular || [])];
        return { success: true, data };
    } catch (error) {
        return { success: false, msg: "Could not fetch announcements" };
    }
}

// Pin/unpin a post (admin only)
export const togglePinPost = async (postId, communityId, userId) => {
    try {
        // Check if user can pin posts
        const canPost = await canPostAnnouncement(userId, communityId);
        if (!canPost.success || !canPost.data) {
            return { success: false, msg: "You don't have permission to pin posts" };
        }

        // Get current post
        const { data: currentPost, error: fetchError } = await Supabase
            .from('posts')
            .select('is_pinned')
            .eq('id', postId)
            .single();

        if (fetchError) {
            return { success: false, msg: "Could not fetch post" };
        }

        // Toggle pin status
        const newIsPinned = !currentPost.is_pinned;
        
        const { data, error } = await Supabase
            .from('posts')
            .update({ is_pinned: newIsPinned })
            .eq('id', postId)
            .select()
            .single();

        if (error) {
            return { success: false, msg: "Could not update post" };
        }
        return { success: true, data: data };
    } catch (error) {
        return { success: false, msg: "Could not update post" };
    }
}


export const fetchPosts = async (limit=10, userId=null)=>{
    try{
        if(userId){
            const { data, error } = await Supabase
                .from('posts')
                .select(`
                    *,
                    user: users ( id, name, image ),
                    community: communities ( name, is_linkallowed ),
                    postLikes (*),
                    comments (count)
                `)
                .eq('userId', userId)
                .eq('hidden', false)
                .order('created_at', {ascending: false })
                .limit(limit);
            if(error){
                return {success: false, msg: "Could not fetch the posts"};
            }
            return {success: true, data: data};
        }else{
            const { data, error } = await Supabase
                .from('posts')
                .select(`
                    *,
                    user: users ( id, name, image ),
                    community: communities ( name, is_linkallowed ),
                    postLikes (*),
                    comments (count)
                `)
                .eq('hidden', false)
                .order('created_at', {ascending: false })
                .limit(limit);
            if(error){
                return {success: false, msg: "Could not fetch the posts"};
            }
            return {success: true, data: data};
        }
    }catch(error){
        return {success: false, msg: "Could not fetch the posts"};
    }
}

// New cursor-based pagination function
export const fetchPostsWithCursor = async (limit=10, userId=null, cursor=null)=>{
    try{
        // Ensure limit is a positive integer and not too large
        const safeLimit = Math.min(Math.max(1, parseInt(limit) || 10), 50); // Max 50 posts per request
        // If this is the first page (no cursor), fetch pinned posts first
        if (!cursor) {
            // First, get pinned posts
            let pinnedQuery = Supabase
                .from('posts')
                .select(`
                    *,
                    user: users ( id, name, image ),
                    community: communities ( name, is_linkallowed ),
                    postLikes (*),
                    comments (count)
                `)
                .eq('hidden', false)
                .eq('is_pinned', true)
                .order('created_at', {ascending: false });

            if(userId){
                pinnedQuery = pinnedQuery.eq('userId', userId);
            }

            const { data: pinnedPosts, error: pinnedError } = await pinnedQuery;

            if (pinnedError) {
                return { success: false, msg: "Could not fetch pinned posts" };
            }

            // Then get regular posts to fill the remaining limit
            const remainingLimit = Math.max(0, safeLimit - (pinnedPosts?.length || 0));
            
            // If we already have enough pinned posts, don't fetch regular posts
            if (remainingLimit <= 0) {
                const normalizedData = (pinnedPosts || []).slice(0, limit).map((post) => ({
                    ...post,
                    comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0 
                        ? post.comments 
                        : [{count: 0}]
                }));
                
                return { 
                    success: true, 
                    data: normalizedData,
                    nextCursor: normalizedData.length > 0 ? normalizedData[normalizedData.length - 1].created_at : null,
                    hasMore: (pinnedPosts?.length || 0) >= safeLimit
                };
            }
            
            let regularQuery = Supabase
                .from('posts')
                .select(`
                    *,
                    user: users ( id, name, image ),
                    community: communities ( name, is_linkallowed ),
                    postLikes (*),
                    comments (count)
                `)
                .eq('hidden', false)
                .eq('is_pinned', false)
                .order('created_at', {ascending: false })
                .limit(remainingLimit);

            if(userId){
                regularQuery = regularQuery.eq('userId', userId);
            }

            const { data: regularPosts, error: regularError } = await regularQuery;

            if (regularError) {
                return { success: false, msg: "Could not fetch regular posts" };
            }

            // Combine pinned and regular posts, but ensure we don't exceed the limit
            const pinnedCount = pinnedPosts?.length || 0;
            const regularCount = regularPosts?.length || 0;
            const allPosts = [
                ...(pinnedPosts || []).slice(0, safeLimit), // Ensure pinned posts don't exceed limit
                ...(regularPosts || []).slice(0, Math.max(0, safeLimit - pinnedCount)) // Fill remaining slots
            ].slice(0, safeLimit); // Final safety check - never exceed limit

            // Normalize comment structure
            const normalizedData = allPosts.map((post) => ({
                ...post,
                comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0 
                    ? post.comments 
                    : [{count: 0}]
            }));

            // Determine if there are more posts
            // Check if we got the full limit (meaning there might be more)
            const hasMore = normalizedData.length >= safeLimit;
            const nextCursor = hasMore && normalizedData.length > 0 ? normalizedData[normalizedData.length - 1].created_at : null;

            return { 
                success: true, 
                data: normalizedData,
                nextCursor,
                hasMore
            };
        } else {
            // For subsequent pages, use normal ordering
            let query = Supabase
                .from('posts')
                .select(`
                    *,
                    user: users ( id, name, image ),
                    community: communities ( name, is_linkallowed ),
                    postLikes (*),
                    comments (count)
                `)
                .eq('hidden', false)
                .order('created_at', {ascending: false })
                .lt('created_at', cursor)
                .limit(safeLimit);

            if(userId){
                query = query.eq('userId', userId);
            }

            const { data, error } = await query;

            if (error) {
                return { success: false, msg: "Could not fetch posts" };
            }

            // Normalize comment structure
            const normalizedData = data.map((post) => ({
                ...post,
                comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0 
                    ? post.comments 
                    : [{count: 0}]
            }));

            // Determine if there are more posts
            const hasMore = data.length >= safeLimit;
            const nextCursor = hasMore && data.length > 0 ? data[data.length - 1].created_at : null;

            return { 
                success: true, 
                data: normalizedData,
                nextCursor,
                hasMore
            };
        }
    } catch (error) {
        return { success: false, msg: "Could not fetch posts" };
    }
}

// Fetch deal posts with optional cursor pagination
export const fetchDealPosts = async (limit = 10, cursor = null) => {
    try{
        let query = Supabase
            .from('posts')
            .select(`
                *,
                user: users ( id, name, image ),
                community: communities ( name, is_linkallowed ),
                postLikes (*),
                comments (count)
            `)
            .eq('hidden', false)
            .eq('is_deal', true)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (cursor) {
            query = query.lt('created_at', cursor);
        }

        const { data, error } = await query;

        if (error) {
            return { success: false, msg: "Could not fetch deals" };
        }

        const normalizedData = (data || []).map((post) => ({
            ...post,
            comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0
                ? post.comments
                : [{ count: 0 }]
        }));

        const hasMore = (data || []).length === limit;
        const nextCursor = hasMore && data && data.length > 0 ? data[data.length - 1].created_at : null;

        return {
            success: true,
            data: normalizedData,
            hasMore,
            nextCursor,
        };
    }catch(error){
        return {success: false, msg: "Could not fetch deals"};
    }
}

export const fetchPostDetails = async (postId)=>{
    try{
        const { data, error } = await Supabase
        .from('posts')
        .select(`
            *,
            user: users ( id, name, image ),
            community: communities ( name, is_linkallowed ),
            postLikes (*),
            comments (*, user: users(id, name, image))
        `)
        .eq('id', postId)
        .eq('hidden', false)
        .order("created_at", { ascending: false, foreignTable: "comments"})
        .single();

        

        if(error){
            return {success: false, msg: "Could not fetch the post"};
        }
        return {success: true, data: data};

    }catch(error){
        return {success: false, msg: "Could not fetch the post"};
    }
}

export const createPostLike = async (postLike)=>{
    try{
        
        const { data, error } = await Supabase
        .from('postLikes')
        .insert(postLike)
        .select()
        .single();

        if(error){
            return {success: false, msg: "Could not like this post"};
        }
        return {success: true, data: data};

    }catch(error){
        return {success: false, msg: "Could not like this post"};
    }
}
export const removePostLike = async (postId, userId)=>{
    try{
        
        const { error } = await Supabase
        .from('postLikes')
        .delete()
        .eq('userId', userId)
        .eq('postId', postId)

        if(error){
            return {success: false, msg: "Could not remove post like"};
        }
        return {success: true, data: {postId, userId}};

    }catch(error){
        return {success: false, msg: "Could not remove post like"};
    }
}


export const createComment = async (comment)=>{
    try{
        
        const { data, error } = await Supabase
        .from('comments')
        .insert(comment)
        .select()
        .single();

        if(error){
            return {success: false, msg: "Could not create your comment"};
        }
        return {success: true, data: data};

    }catch(error){
        return {success: false, msg: "Could not create your comment"};
    }
}


export const removeComment = async (commentId)=>{
    try{
        
        const { error } = await Supabase
        .from('comments')
        .delete()
        .eq('id', commentId)

        if(error){
            return {success: false, msg: "Could not remove the comment"};
        }
        return {success: true, data: {commentId}};

    }catch(error){
        return {success: false, msg: "Could not remove the comment"};
    }
}

export const removePost = async (postId)=>{
    try{
        
        const { error } = await Supabase
        .from('posts')
        .delete()
        .eq('id', postId)

        if(error){
            return {success: false, msg: "Could not remove the post"};
        }
        return {success: true, data: {postId}};

    }catch(error){
        return {success: false, msg: "Could not remove the post"};
    }
}